import { useState } from 'react';

interface Cashier {
  id: string;
  name: string;
  employeeId: string;
  role: string;
  avatar: string;
  status: 'active' | 'break' | 'offline';
}

interface ChangeCashierModalProps {
  onClose: () => void;
  currentCashier: string;
  onCashierChange: (cashierName: string) => void;
}

export default function ChangeCashierModal({ onClose, currentCashier, onCashierChange }: ChangeCashierModalProps) {
  const [selectedCashier, setSelectedCashier] = useState<Cashier | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const cashiers: Cashier[] = [
    { id: '1', name: 'Sarah Johnson', employeeId: 'CSH-042', role: 'Senior Cashier', avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20cashier%20with%20friendly%20smile%20wearing%20retail%20uniform%20in%20modern%20store%20environment%20with%20clean%20simple%20background&width=200&height=200&seq=cashier1&orientation=squarish', status: 'active' },
    { id: '2', name: 'Michael Chen', employeeId: 'CSH-038', role: 'Cashier', avatar: 'https://readdy.ai/api/search-image?query=professional%20male%20cashier%20with%20welcoming%20expression%20wearing%20retail%20uniform%20in%20bright%20store%20setting%20with%20simple%20background&width=200&height=200&seq=cashier2&orientation=squarish', status: 'active' },
    { id: '3', name: 'Emily Rodriguez', employeeId: 'CSH-051', role: 'Cashier', avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20cashier%20with%20confident%20smile%20wearing%20retail%20uniform%20in%20contemporary%20store%20with%20clean%20background&width=200&height=200&seq=cashier3&orientation=squarish', status: 'break' },
    { id: '4', name: 'James Wilson', employeeId: 'CSH-029', role: 'Lead Cashier', avatar: 'https://readdy.ai/api/search-image?query=professional%20male%20lead%20cashier%20with%20experienced%20look%20wearing%20retail%20uniform%20in%20modern%20store%20environment%20with%20simple%20background&width=200&height=200&seq=cashier4&orientation=squarish', status: 'active' },
    { id: '5', name: 'Lisa Anderson', employeeId: 'CSH-045', role: 'Cashier', avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20cashier%20with%20warm%20smile%20wearing%20retail%20uniform%20in%20bright%20store%20setting%20with%20clean%20background&width=200&height=200&seq=cashier5&orientation=squarish', status: 'offline' },
    { id: '6', name: 'David Martinez', employeeId: 'CSH-033', role: 'Cashier', avatar: 'https://readdy.ai/api/search-image?query=professional%20male%20cashier%20with%20friendly%20demeanor%20wearing%20retail%20uniform%20in%20contemporary%20store%20with%20simple%20background&width=200&height=200&seq=cashier6&orientation=squarish', status: 'active' },
  ];

  const handleSelectCashier = (cashier: Cashier) => {
    if (cashier.employeeId === currentCashier) {
      setError('This cashier is already logged in');
      return;
    }
    setSelectedCashier(cashier);
    setPin('');
    setError('');
  };

  const handlePinInput = (digit: string) => {
    if (pin.length < 6) {
      setPin(pin + digit);
    }
  };

  const handlePinDelete = () => {
    setPin(pin.slice(0, -1));
  };

  const handlePinClear = () => {
    setPin('');
    setError('');
  };

  const handleVerify = () => {
    if (!selectedCashier || pin.length !== 6) return;

    setIsVerifying(true);
    setError('');

    // Simulate PIN verification (in real app, verify against backend)
    setTimeout(() => {
      // For demo, accept PIN "123456" for all cashiers
      if (pin === '123456') {
        onCashierChange(selectedCashier.name);
        onClose();
      } else {
        setError('Invalid PIN. Please try again.');
        setPin('');
      }
      setIsVerifying(false);
    }, 800);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'break': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Available';
      case 'break': return 'On Break';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] min-h-[300px] overflow-hidden flex flex-col">
        <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Change Cashier</h2>
            <p className="text-sm text-gray-300 whitespace-nowrap">Select a cashier and enter PIN to switch</p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex">
          {/* Left Panel - Cashier Selection */}
          <div className="flex-1 p-6 border-r border-gray-200">
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
              <i className="ri-information-line text-blue-600 text-lg"></i>
              <p className="text-sm text-blue-800 whitespace-nowrap">Current cashier: <span className="font-semibold">{currentCashier}</span></p>
            </div>

            <h3 className="text-sm font-bold text-gray-900 mb-4 whitespace-nowrap">Available Cashiers</h3>
            
            <div className="grid grid-cols-2 gap-4">
              {cashiers.map((cashier) => (
                <button
                  key={cashier.id}
                  onClick={() => handleSelectCashier(cashier)}
                  disabled={cashier.status === 'offline' || cashier.employeeId === currentCashier}
                  className={`p-4 rounded-lg border-2 transition-all cursor-pointer text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                    selectedCashier?.id === cashier.id
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  } ${cashier.employeeId === currentCashier ? 'bg-gray-100' : ''}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
                        <img 
                          src={cashier.avatar} 
                          alt={cashier.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className={`absolute bottom-0 right-0 w-4 h-4 ${getStatusColor(cashier.status)} rounded-full border-2 border-white`}></div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{cashier.name}</p>
                      <p className="text-xs text-gray-600 whitespace-nowrap">{cashier.employeeId}</p>
                      <p className="text-xs text-gray-500 whitespace-nowrap">{cashier.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                      cashier.status === 'active' ? 'bg-green-100 text-green-700' :
                      cashier.status === 'break' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    } whitespace-nowrap`}>
                      {getStatusText(cashier.status)}
                    </span>
                    {cashier.employeeId === currentCashier && (
                      <span className="text-xs font-semibold text-teal-600 whitespace-nowrap">Current</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right Panel - PIN Entry */}
          <div className="w-96 bg-gray-50 p-6 flex flex-col">
            {selectedCashier ? (
              <>
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 whitespace-nowrap">Enter PIN to Confirm</h3>
                  
                  <div className="bg-white rounded-lg p-4 border-2 border-gray-200 mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
                        <img 
                          src={selectedCashier.avatar} 
                          alt={selectedCashier.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{selectedCashier.name}</p>
                        <p className="text-xs text-gray-600 whitespace-nowrap">{selectedCashier.employeeId}</p>
                        <p className="text-xs text-gray-500 whitespace-nowrap">{selectedCashier.role}</p>
                      </div>
                    </div>
                  </div>

                  {/* PIN Display */}
                  <div className="flex items-center justify-center gap-2 mb-6">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center ${
                          pin.length > index ? 'border-teal-500 bg-teal-50' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {pin.length > index && (
                          <div className="w-3 h-3 bg-teal-600 rounded-full"></div>
                        )}
                      </div>
                    ))}
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <i className="ri-error-warning-line text-red-600 text-lg"></i>
                      <p className="text-sm text-red-800 whitespace-nowrap">{error}</p>
                    </div>
                  )}

                  {/* PIN Keypad */}
                  <div className="grid grid-cols-3 gap-3">
                    {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                      <button
                        key={digit}
                        onClick={() => handlePinInput(digit)}
                        disabled={pin.length >= 6 || isVerifying}
                        className="h-14 bg-white hover:bg-gray-100 border-2 border-gray-200 rounded-lg text-xl font-bold text-gray-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {digit}
                      </button>
                    ))}
                    <button
                      onClick={handlePinClear}
                      disabled={pin.length === 0 || isVerifying}
                      className="h-14 bg-white hover:bg-gray-100 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => handlePinInput('0')}
                      disabled={pin.length >= 6 || isVerifying}
                      className="h-14 bg-white hover:bg-gray-100 border-2 border-gray-200 rounded-lg text-xl font-bold text-gray-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      0
                    </button>
                    <button
                      onClick={handlePinDelete}
                      disabled={pin.length === 0 || isVerifying}
                      className="h-14 bg-white hover:bg-gray-100 border-2 border-gray-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <i className="ri-delete-back-2-line text-xl text-gray-700"></i>
                    </button>
                  </div>
                </div>

                <div className="mt-auto">
                  <button
                    onClick={handleVerify}
                    disabled={pin.length !== 6 || isVerifying}
                    className={`w-full py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                      pin.length !== 6 || isVerifying
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer'
                    }`}
                  >
                    {isVerifying ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line mr-2"></i>
                        Confirm Switch
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-3 whitespace-nowrap">
                    Demo PIN: 123456
                  </p>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <i className="ri-user-line text-6xl mb-3"></i>
                  <p className="text-sm whitespace-nowrap">Select a cashier to continue</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <i className="ri-shield-check-line mr-2 text-teal-600"></i>
            <span className="whitespace-nowrap">Secure PIN authentication required</span>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
